import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import Dropdown from 'components/forms/basefields/Dropdown';
import Checkbox from 'components/forms/basefields/Checkbox';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faLock, faLockOpen, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { FormType } from '../../views/forms/FormTypes';
import { ContractJobScheduleTaskType } from './ContractTypes';

const ContractScheduleTaskRows: React.FC<{
    tasks: ContractJobScheduleTaskType[];
    setTasks: React.Dispatch<React.SetStateAction<ContractJobScheduleTaskType[]>>;
    forms: FormType[];
    showInspectionOption?: boolean;
    inspectionError?: string;
}> = ({
    tasks,
    setTasks,
    forms,
    showInspectionOption,
    inspectionError,
}) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();

    // Only show the form dropdown when the forms module is active
    const formsEnabled = activeFeatures.includes('forms_base');

    // Set the saved tasks which render above the inspection checkbox
    const savedVisibleTasks = tasks.filter(task => !task.deleted && !task.include_asset_inspection && task.id);

    // Set the newly added tasks which render below the inspection checkbox
    const newVisibleTasks = tasks.filter(task => !task.deleted && !task.include_asset_inspection && !task.id);

    // Get the inspection task of the schedule
    const inspectionTask = tasks.find(task => task.include_asset_inspection && !task.deleted);

    // Update one value of a task row
    const updateTask = (taskIndex: number, values: Partial<ContractJobScheduleTaskType>) => {
        setTasks(previousTasks => previousTasks.map((task, index) =>
            index === taskIndex ? { ...task, ...values } : task
        ));
    };

    // Add an empty task row
    const addTask = () => {
        setTasks(previousTasks => [...previousTasks, { description: '', required: false, form: null }]);
    };

    // Remove a task row, existing tasks are flagged as deleted
    const removeTask = (taskIndex: number) => {
        setTasks(previousTasks => previousTasks
            .map((task, index) => index === taskIndex ? { ...task, deleted: true } : task)
            .filter(task => task.id || !task.deleted)
        );
    };

    // Add or remove the inspection task of the schedule
    const toggleInspectionTask = () => {
        if (inspectionTask) {
            const inspectionTaskIndex = tasks.indexOf(inspectionTask);
            removeTask(inspectionTaskIndex);
        } else {
            setTasks(previousTasks => [...previousTasks, {
                description: t('contract.job_schedule.inspection_task_default_description'),
                required: false,
                form: null,
                include_asset_inspection: true,
            }]);
        }
    };

    // Render one editable task row
    const renderTaskRow = (task: ContractJobScheduleTaskType) => {
        const taskIndex = tasks.indexOf(task);
        return (
            <div key={task.id ?? `new-${taskIndex}`} className='contract-schedule-task-row'>
                <div className='task-description-line'>
                    <input
                        type='text'
                        value={task.description}
                        placeholder={t('contract.job_schedule.task_placeholder')}
                        onChange={(event) => updateTask(taskIndex, { description: event.target.value })}
                    />
                    {formsEnabled && (
                        <div className='task-form-select'>
                            <Dropdown<FormType>
                                options={forms}
                                name={`task-form-${taskIndex}`}
                                disabled_selected={t('contract.job_schedule.task_form_disabled_selected')}
                                selectedOption={forms.find(form => form.id === task.form) || null}
                                onChange={(selectedValue) => updateTask(taskIndex, { form: selectedValue === 'none' ? null : Number(selectedValue) })}
                                selectionFormat={(option) => `${option.name}`}
                                optionFormat='name'
                                showSearch={false}
                                allowNoneOption={true}
                            />
                        </div>
                    )}
                    <div className={`task-required-icon ${task.required ? 'active' : ''} tooltip-icon`}
                         onClick={() => updateTask(taskIndex, { required: !task.required })}>
                        <FontAwesomeIcon icon={task.required ? faLock : faLockOpen} />
                        <span className="tooltip">{t('job.tasks.task_required_tooltip')}</span>
                    </div>
                    <div className='delete-icon tooltip-icon'
                         onClick={() => removeTask(taskIndex)}>
                        <FontAwesomeIcon icon={faTrash} />
                        <span className="tooltip">{t('general.delete')}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='contract-schedule-tasks'>
            {savedVisibleTasks.map(renderTaskRow)}
            {/* The inspection list of the objects of the contract */}
            {(showInspectionOption || inspectionTask) && (
                <div className='contract-schedule-inspection'>
                    <div className='inspection-checkbox-line'>
                        <Checkbox
                            name='include_asset_inspection'
                            label='contract.job_schedule.include_asset_inspection_label'
                            align='left'
                            value={!!inspectionTask}
                            onChange={toggleInspectionTask}
                        />
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">
                                {t('contract.job_schedule.include_asset_inspection_tooltip')}
                            </span>
                        </span>
                    </div>
                    {inspectionTask && (
                        <div className='task-description-line'>
                            <input
                                type='text'
                                value={inspectionTask.description}
                                placeholder={t('contract.job_schedule.inspection_task_placeholder')}
                                onChange={(event) => updateTask(tasks.indexOf(inspectionTask), { description: event.target.value })}
                            />
                            <div className='task-form-select'>
                                <Dropdown<FormType>
                                    options={forms}
                                    name='inspection-task-form'
                                    disabled_selected={t('contract.job_schedule.inspection_form_disabled_selected')}
                                    selectedOption={forms.find(form => form.id === inspectionTask.form) || null}
                                    onChange={(selectedValue) => updateTask(tasks.indexOf(inspectionTask), { form: selectedValue === 'none' ? null : Number(selectedValue) })}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat='name'
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                            </div>
                            <div className={`task-required-icon ${inspectionTask.required ? 'active' : ''} tooltip-icon`}
                                 onClick={() => updateTask(tasks.indexOf(inspectionTask), { required: !inspectionTask.required })}>
                                <FontAwesomeIcon icon={inspectionTask.required ? faLock : faLockOpen} />
                                <span className="tooltip">{t('job.tasks.task_required_tooltip')}</span>
                            </div>
                        </div>
                    )}
                    {inspectionError && (
                        <div className='error-message'>
                            {t(inspectionError)}
                        </div>
                    )}
                </div>
            )}

            {newVisibleTasks.map(renderTaskRow)}

            <IconButton
                type='button'
                onClick={addTask}
                icon={<FontAwesomeIcon icon={faPlus} />}
                label='contract.job_schedule.add_task_label'
                size='extra-small'
            />
        </div>
    );
};

export default ContractScheduleTaskRows;
